import { useGetJwtLoginIdp } from '@framework/api-client/api/auth/jwt-controller/jwt-controller.ts';
import { message } from 'antd';
import qs from 'qs';
import { useDispatch } from 'react-redux';
import { type AuthSource } from '../../AuthSource.ts';
import { getNetworkErrorMessage } from '../../http/errors/getNetworkErrorMessage.ts';
import { AuthActions } from '../../state/AuthSlice.tsx';

export interface IUseIdpAutoLoginOptions {
  /**
   * Abilita la chiamata di auto-login. Se `false` la richiesta API non
   * viene mai eseguita, a prescindere dalla presenza del parametro `idp`
   * nell'URL. Il tema deve passare `true` solo quando la strategia IDP è
   * attiva o quando il login IDP è previsto dal tema stesso.
   */
  enabled?: boolean;
}

/**
 * Effettua il login automatico con l'IDP presente nell'URL.
 * Viene utilizzato in seguito alla Redirezione automatica dell'IDP da parte
 * della componente di backend `GET /jwt/login/:idp`.
 */
export function useIdpAutoLogin(
  source: AuthSource,
  options: IUseIdpAutoLoginOptions = {},
) {
  const { enabled = true } = options;
  const dispatch = useDispatch();

  // Estrae il parametro `idp` dall'URL
  const queryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const idp =
    queryString?.idp && typeof queryString.idp === 'string'
      ? queryString.idp
      : null;

  const loginResult = useGetJwtLoginIdp(idp!, {
    query: {
      enabled: enabled && idp != null,
      onError: (error: any) => {
        // In caso di errore lo segnaliamo all'utente
        message.error(
          getNetworkErrorMessage(
            error,
            'Si è verificato un errore durante il login. Si prega di riprovare.',
          ),
        );
      },
      // Callback di successo
      onSuccess: (result: any) => {
        dispatch(
          AuthActions.loginSucceed({
            strategy: 'jwt',
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            logoutUrls: result.logoutUrls ?? [],
          }),
        );
      },
    },
    request: {
      headers: {
        'X-Login-Provenienza': source,
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  });

  return loginResult;
}
